import React, { FC, useState } from "react";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { getEnumDisplayNameByConstant } from "../../../helpers/utils/EnumUtils";
import { formatAsCurrency } from "../../../helpers/utils/TextUtils";
import { useParams } from "react-router-dom";
import { formatValueAsDate } from "../../../helpers/utils/DateUtils";
import { FaDollarSign } from "react-icons/fa";
import OverridePriceModal from "../modal/OverridePriceModal";
import { IoIosArrowForward } from "react-icons/io";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface ProductPricingDetailProps {
    productId?: string | null;
    orderData?: OrderData;
}

interface PricingItem {
    type: string;
    basePrice: number;
    overridePrice?: number;
    calculatedPrice?: number;
    baseDisplayPrice?: number;
    overrideDisplayPrice?: number;
    calculatedDisplayPrice?: number;
    lockedToCustomer?: boolean;
    manualOverride?: boolean;
    baseDiscount?: number;
    baseDiscountPerParcel?: number;
    parcelCount?: number;
    id: string;
}

interface OrderData {
    data?: {
        error?: string;
        totalPrice?: number;
        revenueEnabled?: boolean;
        orderPaymentStatus?: string;
        paidDate?: string;
        products?: PricingItem[];
        totalDisplayPrice?: number;
    };
}

interface EnumItem {
    constant: string;
    value: string;
}

interface EnumsData {
    [key: string]: EnumItem[];
}

const OrderPricingDetail: FC<ProductPricingDetailProps> = ({ productId, orderData }) => {
    const { data: enums } = useGetEnumsQuery("");
    const [showOverRidePriceModal, setShowOverRidePriceModal] = useState(false);
    const [existingOverridePrice, setExistingOverridePrice] = useState<any>();
    const [existingPrice, setExistingPrice] = useState("");
    const [existingOverrideDisplayPrice, setExistingOverrideDisplayPrice] = useState<any>();
    const [existingDisplayPrice, setExistingDisplayPrice] = useState("");
    const [productsId, setProductId] = useState("");
    const [isCollapsed, setIsCollapsed] = useState(false);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <div className="bg-text-1 shadow-[0_3px_10px_#0003] text-sm mt-3 rounded-md ">
            <div className={`flex gap-2 items-center p-3 ${isCollapsed ? "border-b border-surface-7" : ""}`}>
                <div className="text-surface-0 font-semibold w-80">Pricing Information</div>
                <div className="flex gap-3 items-center grow w-full justify-between">
                    <div className="">
                        {orderData?.data && orderData?.data?.error === undefined ? <span
                            className={"mt-0"}>{orderData.data?.totalPrice != null ? '$' + orderData.data?.totalPrice.toFixed(2) : '-'}</span>
                            :
                            <span className="loading w-100">
                                <div className="bar" />
                            </span>
                        }
                    </div>

                    {orderData?.data && orderData?.data?.error === undefined &&
                        <button
                            className={orderData?.data && orderData?.data?.revenueEnabled === true ? " bg-surface-0 border border-[#0000] rounded-[11px] shadow-[0_0_4px_#0003] text-text-0 m-[7px_4px] px-[14px] py-[5px]" : " bg-[#969da4] border border-[#0000] rounded-[11px] shadow-[0_0_4px_#0003] text-text-0 m-[7px_4px] px-[14px] py-[5px]"}
                            title={orderData?.data && orderData?.data?.revenueEnabled === true ? "Enabled Revenue" : "Disabled Revenue"}>
                            <FaDollarSign /></button>
                    }


                    <button
                        className="text-highlight-5 focus:outline-none"
                        onClick={toggleCollapse}
                    >
                        <IoIosArrowForward
                            className={`text-highlight-0 text-2xl cursor-pointer transition-transform  duration-300 ${isCollapsed ? "rotate-90" : ""
                                }`}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>

            {isCollapsed && (
                <div className="mt-3 rounded-lg overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-2 font-semibold">Payment Status</th>
                                <th></th>
                                <th className="p-2">
                                    <div
                                        className="col-6"
                                        title={
                                            orderData?.data && orderData?.data?.error === undefined && orderData?.data?.paidDate !== null
                                                ? formatValueAsDate(orderData?.data?.paidDate ?? "") ?? "" // Ensures string or undefined
                                                : undefined
                                        }
                                    >
                                        {orderData?.data && orderData?.data?.error === undefined ? (
                                            <span className={orderData?.data ? "bg-highlight-4 text-text-0 p-1 text-xs  rounded-md" : ""}>
                                                {orderData?.data?.orderPaymentStatus !== null ? (
                                                    Array.isArray(enums?.["OrderPaymentStatus"]) && enums?.["OrderPaymentStatus"].length > 0 ? (
                                                        enums?.["OrderPaymentStatus"].map((curEnum: any) =>
                                                            curEnum.constant === orderData?.data?.orderPaymentStatus ? curEnum.value : null
                                                        )
                                                    ) : "Unknown"
                                                ) : "Unknown"}
                                            </span>
                                        ) : null}
                                    </div>
                                </th>

                            </tr>
                        </thead>
                        <tbody>

                            <tr className="border-t border-surface-7">
                                <td className="p-2 font-semibold">Product</td>
                                <td className="p-2 font-semibold">Base</td>
                                <td className="p-2 font-semibold">Price</td>
                                <td className="p-2 font-semibold">Display</td>
                            </tr>
                            {orderData?.data && orderData?.data.error === undefined ? (
                                orderData?.data?.products && [...orderData?.data?.products]
                                    .sort((a, b) => a.type.localeCompare(b.type))
                                    .map((curProduct: any) => (
                                        <tr key={curProduct.id} className="border-t border-surface-7">
                                            <td className="p-2">{getEnumDisplayNameByConstant(enums?.["ProductType"], curProduct.type)}</td>
                                            <td className="p-2">{curProduct?.basePrice != null
                                                ? formatAsCurrency(curProduct.basePrice.toString())
                                                : "-"}
                                            </td>
                                            <td className="p-2">
                                                {(curProduct.overridePrice ?? curProduct.calculatedPrice ?? curProduct.basePrice) != null
                                                    ? formatAsCurrency(String(curProduct.overridePrice ?? curProduct.calculatedPrice ?? curProduct.basePrice))
                                                    : "-"}
                                            </td>

                                            <td className="p-2 flex justify-between">
                                                {(curProduct.overrideDisplayPrice ?? curProduct.calculatedDisplayPrice ?? curProduct.baseDisplayPrice) != null
                                                    ? formatAsCurrency(String(curProduct.overrideDisplayPrice ?? curProduct.calculatedDisplayPrice ?? curProduct.baseDisplayPrice))
                                                    : "-"}
                                                {hasPermission(["OVRPRICE-V"]) &&
                                                    <button className="bg-highlight-0 p-1 pl-2 pr-2 rounded-[11px] text-text-0 font-semibold text-xs"
                                                        title={"Override Price"}
                                                        onClick={() => {
                                                            setExistingPrice(curProduct?.calculatedPrice?.toFixed(2) ?? curProduct.basePrice?.toFixed(2));
                                                            setExistingOverridePrice(curProduct?.overridePrice);
                                                            setExistingDisplayPrice(curProduct?.baseDisplayPrice?.toFixed(2))
                                                            setExistingOverrideDisplayPrice(curProduct?.overrideDisplayPrice);
                                                            setProductId(curProduct?.id);
                                                            setShowOverRidePriceModal(true);
                                                        }}>Override
                                                    </button>}
                                            </td>
                                        </tr>
                                    ))
                            ) : null}

                            <tr className="border-t border-surface-7">
                                <td className="p-2 font-semibold">Total</td>
                                <td className="p-2"> </td>
                                <td className="p-2">{orderData?.data?.totalPrice != null ? formatAsCurrency(String(orderData?.data?.totalPrice)) : '-'}</td>
                                <td className="p-2">{orderData?.data?.totalDisplayPrice != null ? formatAsCurrency(String(orderData?.data?.totalDisplayPrice)) : '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

            )}



            {
                showOverRidePriceModal && <OverridePriceModal show={showOverRidePriceModal}
                    setShow={setShowOverRidePriceModal}
                    existingPrice={existingPrice}
                    existingOverridePrice={existingOverridePrice}
                    existingDisplayPrice={existingDisplayPrice}
                    existingOverrideDisplayPrice={existingOverrideDisplayPrice}
                    productId={productsId}
                />
            }
        </div >
    );
};

export default OrderPricingDetail;
